var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, Fragment } from 'react';
import styled, { useTheme } from 'styled-components';
import StarRatings from 'react-star-ratings';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LoadingButton from 'components/Shared/LoadingButton';
import { usePageData, useAnalytics } from 'hooks';
import graphQLClient from 'utils/graphql-client';
import { FEEDBACK_MUTATION } from 'graphql/mutations';
import { clickedIds } from 'constants/analytics';
import * as Sentry from '@sentry/nextjs';
const Headline = styled.h2 `
  width: 100%;
`;
const Subtitle = styled.p `
  color: ${({ theme }) => theme.palette.grey[700]};
  margin-bottom: 16px;

  ${({ center }) => center &&
    `
    margin: 16px auto;
    width: 80%;
  `}
`;
const Container = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px 8px;
  background: ${({ hasGreyBackground, theme }) => hasGreyBackground ? theme.backgrounds.light : '#fff'};
  min-height: 430px;

  ${({ center }) => center &&
    `
    align-items: center;
    text-align: center;
  `}
`;
const Input = styled.textarea `
  background: ${({ theme, hasLightBackground }) => hasLightBackground ? '#fff' : theme.palette.grey[100]};
  border-color: transparent;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  width: 100%;
  height: 120px;
  outline: none;
`;
const RatingBox = styled.div `
  text-align: center;

  .error-message {
    left: 50%;
    top: 90%;
    transform: translateX(-50%);
  }
`;
const Form = styled.form `
  width: 100%;
`;
const FormControl = styled.div `
  position: relative;
  margin-bottom: 16px;

  ${({ type }) => type === 'checkbox' &&
    `
    display: flex;
    margin-bottom: 24px;

    input {
      margin-right: 8px;
    }
  `}
`;
const Notice = styled.p `
  font-size: 14px;
  margin-bottom: 0;
  text-align: left;

  a {
    text-decoration: none;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;
const Button = styled((props) => React.createElement(LoadingButton, Object.assign({}, props))) `
  padding: 12px 16px;
  width: 100%;
  text-align: center;

  ${({ $center }) => $center &&
    `
    width: 200px;
  `}
`;
const ErrorMessage = styled.p `
  position: absolute;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.error.main};
  margin-bottom: 0;
  bottom: 0;
  transform: translateY(85%);
`;
const Checkbox = styled.input `
  margin-top: 6px;
`;
const FeedbackForm = ({ data: { headline, subtitle, ratingEnabled }, center, hasGreyBackground = false, }) => {
    const { productId, createdBy: { name: companyName }, } = usePageData();
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const { register, handleSubmit, errors, formState, control, setValue, getValues, } = useForm();
    const { t } = useTranslation();
    const { analytics } = useAnalytics();
    const onSubmit = (_a) => __awaiter(void 0, [_a], void 0, function* ({ rating = null, feedback, privacyAccepted }) {
        const visitorId = analytics.getState().user.anonymousId;
        try {
            yield graphQLClient.request(FEEDBACK_MUTATION, {
                productId,
                visitorId,
                rating,
                feedback,
                privacyAccepted,
            });
            setSuccess(true);
        }
        catch (error) {
            Sentry.captureException(error, {
                extra: {
                    productId,
                    feedback,
                    rating,
                    privacyAccepted,
                    request: 'FEEDBACK_MUTATION',
                    userFacing: 'Generic error is shown to the user after submitting the feedback.',
                    file: 'FeedbackForm.tsx',
                    method: 'onSubmit',
                },
            });
            setError(true);
        }
    });
    const { palette } = useTheme();
    return (React.createElement(Container, { hasGreyBackground: hasGreyBackground, center: center },
        success && (React.createElement(Fragment, null,
            React.createElement(Headline, null, t('success-headline')),
            React.createElement(Subtitle, { center: center }, t('success-subtitle', { company: companyName })))),
        error && (React.createElement(Fragment, null,
            React.createElement(Headline, null, t('error-headline')),
            React.createElement(Subtitle, { center: center }, t('error-subtitle')))),
        !success && !error && (React.createElement(Fragment, null,
            React.createElement(Headline, null, headline),
            React.createElement(Subtitle, { center: center }, subtitle),
            React.createElement(Form, null,
                ratingEnabled && (React.createElement(FormControl, null,
                    React.createElement(RatingBox, null,
                        React.createElement(Controller, { control: control, name: "rating", rules: { required: true }, render: ({ onChange, value }) => (React.createElement(StarRatings, { rating: value, starEmptyColor: palette.grey[300], starRatedColor: palette.primary.main, starHoverColor: palette.primary.main, changeRating: onChange, numberOfStars: 5, name: "rating", starDimension: "24px", disabled: formState.isSubmitting })) }),
                        errors.rating && (React.createElement(ErrorMessage, { className: "error-message" }, t('required-error')))))),
                React.createElement(FormControl, null,
                    React.createElement(Input, { name: "feedback", ref: register({ required: true }), placeholder: t('feedback-placeholder'), disabled: formState.isSubmitting, hasLightBackground: hasGreyBackground }),
                    errors.feedback && (React.createElement(ErrorMessage, { className: "error-message" }, t('required-error')))),
                React.createElement(FormControl, { type: "checkbox" },
                    React.createElement(Checkbox, { name: "privacyAccepted", ref: register({ required: true }), type: "checkbox", disabled: formState.isSubmitting }),
                    React.createElement(Notice, { onClick: (event) => {
                            const target = event.target;
                            if (target.localName === 'a')
                                return;
                            const { privacyAccepted } = getValues();
                            setValue('privacyAccepted', !privacyAccepted, {
                                shouldValidate: true,
                            });
                        }, dangerouslySetInnerHTML: { __html: t('privacy-message') } }),
                    errors.privacyAccepted && (React.createElement(ErrorMessage, null, t('privacy-error')))),
                React.createElement(Button, { "$center": center, loading: formState.isSubmitting, onClick: handleSubmit(onSubmit), "data-tracking-id": clickedIds.feedback }, t('submit')))))));
};
export default FeedbackForm;
